import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Video } from '../../types/Video';
import { RootState } from '../../store/store';
import { createEntityAdapter } from '@reduxjs/toolkit';

const videosAdapter = createEntityAdapter<Video>({
  sortComparer: (a, b) => {
    // Sort by ord if both have it, otherwise fall back to createdAt
    if (a.ord !== undefined && b.ord !== undefined) {
      return a.ord - b.ord;
    }
    // If only one has ord, prioritize the one with ord
    if (a.ord !== undefined && b.ord === undefined) {
      return -1;
    }
    if (a.ord === undefined && b.ord !== undefined) {
      return 1;
    }
    // Both lack ord, sort by createdAt
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  },
});

const initialState = videosAdapter.getInitialState({
  activeVideoId: null as string | null,
});

export const videosSlice = createSlice({
  name: 'videos',
  initialState,
  reducers: {
    videosLoaded: (state, action: PayloadAction<Video[]>) => {
      videosAdapter.setAll(state, action.payload);

      // If there's no active video OR the active video doesn't exist in loaded videos, set one based on priority
      if (
        !state.activeVideoId ||
        !action.payload.find((video) => video.id === state.activeVideoId)
      ) {
        const videos = action.payload;

        // First try to find a NOT_STARTED video
        let targetVideo = videos.find(
          (video) => video.watchState === 'NOT_STARTED'
        );

        // If no NOT_STARTED, try IN_PROGRESS
        if (!targetVideo) {
          targetVideo = videos.find(
            (video) => video.watchState === 'IN_PROGRESS'
          );
        }

        // If no IN_PROGRESS, try COMPLETED
        if (!targetVideo) {
          targetVideo = videos.find(
            (video) => video.watchState === 'COMPLETED'
          );
        }

        // If we found a suitable video, set it as active
        if (targetVideo) {
          state.activeVideoId = targetVideo.id;
        }
      }
    },
    videoReceived: (state, action: PayloadAction<Video>) => {
      videosAdapter.upsertOne(state, action.payload);
    },
    videoUpdated: (state, action: PayloadAction<Video>) => {
      videosAdapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload,
      });
    },
    videoRemoved: (state, action: PayloadAction<string>) => {
      videosAdapter.removeOne(state, action.payload);
    },
    videosCleared: (state) => {
      videosAdapter.removeAll(state);
    },
    activeVideoSet: (state, action: PayloadAction<string | null>) => {
      state.activeVideoId = action.payload;
    },
    activeVideoCleared: (state) => {
      state.activeVideoId = null;
    },
  },
});

// Create adapter selectors
const videosSelectors = videosAdapter.getSelectors(
  (state: RootState) => state.videos || videosAdapter.getInitialState()
);

export const selectVideos = videosSelectors.selectAll;
export const selectVideoById = videosSelectors.selectById;
export const selectVideoIds = videosSelectors.selectIds;
export const selectVideoEntities = videosSelectors.selectEntities;
export const selectTotalVideos = videosSelectors.selectTotal;

// Custom selectors
export const selectVideosBySource = createSelector(
  [selectVideos, (_: RootState, source: string) => source],
  (videos: Video[], source: string) => {
    return videos.filter((video: Video) => video.source === source);
  }
);

// Active video selectors
export const selectActiveVideoId = (state: RootState) =>
  state.videos.activeVideoId;

export const selectActiveVideo = createSelector(
  [selectVideoEntities, selectActiveVideoId],
  (videoEntities, activeVideoId) => {
    return activeVideoId ? videoEntities[activeVideoId] || null : null;
  }
);

export const {
  videosLoaded,
  videoReceived,
  videoUpdated,
  videoRemoved,
  videosCleared,
  activeVideoSet,
  activeVideoCleared,
} = videosSlice.actions;

export default videosSlice.reducer;
